/**
 * 一元配置 ANOVA の事後検定: Holm / Scheffe / Dunnett
 * レガシー ANOVAView の postHocSummary と同型の t 検定（MS 誤差）ベース
 */

import type { ANOVAResult } from './anova';
import { twoTailedPValue } from './tTest';

export function approximateFPValue(fValue: number, df1: number, df2: number): number {
  if (!Number.isFinite(fValue) || df1 <= 0 || df2 <= 0) return NaN;
  const factor = df2 / (df2 + df1 * fValue);
  return Math.max(0, Math.min(1, Math.pow(factor, df2 / 2)));
}

export function resolveControlGroupName(
  groups: Array<{ name: string }>,
  override?: string
): string {
  if (override && groups.some((g) => g.name === override)) return override;
  return groups.find((g) => g.name.toLowerCase() === 'control')?.name ?? groups[0]?.name ?? '';
}

export interface PairwiseComparisonResult {
  leftIndex: number;
  rightIndex: number;
  pValue: number;
  significant: boolean;
}

interface RawPair {
  leftIndex: number;
  rightIndex: number;
  rawP: number;
  tValue: number;
  fValue: number;
}

function computeRawPairwiseTTests(
  result: ANOVAResult,
  pairFilter?: (leftIndex: number, rightIndex: number) => boolean
): RawPair[] {
  const groups = result.groups;
  const k = groups.length;
  const df = result.dfWithin;
  const msw = result.msWithin;
  const pairs: RawPair[] = [];

  for (let i = 0; i < k; i += 1) {
    for (let j = i + 1; j < k; j += 1) {
      if (pairFilter && !pairFilter(i, j)) continue;
      const se =
        groups[i].n > 0 && groups[j].n > 0
          ? Math.sqrt(msw * (1 / groups[i].n + 1 / groups[j].n))
          : 0;
      const diff = groups[j].mean - groups[i].mean;
      const tVal = se > 0 ? Math.abs(diff / se) : 0;
      const rawP = twoTailedPValue(tVal, df);
      const fValue = result.dfBetween > 0 ? (tVal * tVal) / result.dfBetween : 0;
      pairs.push({ leftIndex: i, rightIndex: j, rawP, tValue: tVal, fValue });
    }
  }
  return pairs;
}

const DEFAULT_ALPHA = 0.05;

export function computeHolmAllPairs(
  result: ANOVAResult,
  alpha = DEFAULT_ALPHA
): PairwiseComparisonResult[] {
  const rawPairs = computeRawPairwiseTTests(result);
  const pairCount = rawPairs.length;
  if (pairCount === 0) return [];

  const sorted = [...rawPairs].sort((a, b) => a.rawP - b.rawP);
  const adjustedByKey = new Map<string, number>();
  sorted.forEach((pair, index) => {
    adjustedByKey.set(`${pair.leftIndex}-${pair.rightIndex}`, Math.min(1, (pairCount - index) * pair.rawP));
  });

  return rawPairs.map((pair) => {
    const pValue = adjustedByKey.get(`${pair.leftIndex}-${pair.rightIndex}`) ?? pair.rawP;
    return {
      leftIndex: pair.leftIndex,
      rightIndex: pair.rightIndex,
      pValue,
      significant: pValue < alpha,
    };
  });
}

export function computeHolmSignificancePairs(
  result: ANOVAResult,
  alpha = DEFAULT_ALPHA
): PairwiseComparisonResult[] {
  return computeHolmAllPairs(result, alpha).filter((p) => p.significant);
}

export function computeScheffeAllPairs(
  result: ANOVAResult,
  alpha = DEFAULT_ALPHA
): PairwiseComparisonResult[] {
  return computeRawPairwiseTTests(result).map((pair) => {
    const pValue = approximateFPValue(pair.fValue, result.dfBetween, result.dfWithin);
    return {
      leftIndex: pair.leftIndex,
      rightIndex: pair.rightIndex,
      pValue,
      significant: pValue < alpha,
    };
  });
}

export function computeScheffeSignificancePairs(
  result: ANOVAResult,
  alpha = DEFAULT_ALPHA
): PairwiseComparisonResult[] {
  return computeScheffeAllPairs(result, alpha).filter((p) => p.significant);
}

export function computeDunnettAllPairs(
  result: ANOVAResult,
  controlGroupName: string,
  alpha = DEFAULT_ALPHA
): PairwiseComparisonResult[] {
  const controlIndex = result.groups.findIndex((g) => g.name === controlGroupName);
  if (controlIndex < 0) return [];

  return computeRawPairwiseTTests(
    result,
    (i, j) => i === controlIndex || j === controlIndex
  ).map((pair) => ({
    leftIndex: pair.leftIndex,
    rightIndex: pair.rightIndex,
    pValue: pair.rawP,
    significant: pair.rawP < alpha,
  }));
}

export function computeDunnettSignificancePairs(
  result: ANOVAResult,
  controlGroupName: string,
  alpha = DEFAULT_ALPHA
): PairwiseComparisonResult[] {
  return computeDunnettAllPairs(result, controlGroupName, alpha).filter((p) => p.significant);
}
